// Per-case "last seen" tracking (localStorage). Powers the unread markers on
// the case list/dashboard and the "new since your last visit" divider in the
// case thread. Client-side only by design: it answers "what changed since I
// last looked *from this browser*" — good enough for v1, and upgrading to a
// server-side read state later doesn't change the call sites.

const KEY = 'itms-qc:case-seen';

type SeenMap = Record<string, string>; // caseId → ISO timestamp of newest activity seen

function load(): SeenMap {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as SeenMap) : {};
  } catch {
    return {};
  }
}

/** The activity timestamp the user had seen for this case (null = never opened it). */
export function lastSeenAt(caseId: number): string | null {
  return load()[String(caseId)] ?? null;
}

/** Record that the user has now seen this case up to `upTo` (case updatedAt). */
export function markCaseSeen(caseId: number, upTo: string | null | undefined): void {
  if (!upTo) return;
  try {
    const map = load();
    const existing = map[String(caseId)];
    // Never move the marker backwards (e.g. a stale tab re-mounting).
    if (!existing || new Date(upTo).getTime() > new Date(existing).getTime()) {
      map[String(caseId)] = upTo;
      localStorage.setItem(KEY, JSON.stringify(map));
    }
  } catch {
    // Storage unavailable (private mode etc.) — unread markers just no-op.
  }
}

/** True when the case has activity newer than what this browser has seen. */
export function isUnread(caseId: number, lastActivityAt: string | null | undefined): boolean {
  if (!lastActivityAt) return false;
  const seen = lastSeenAt(caseId);
  if (!seen) return true; // never opened → new to you
  return new Date(lastActivityAt).getTime() > new Date(seen).getTime();
}
